import {
  Menu as MenuIcon,
  Close as CloseIcon,
  AdminPanelSettings,
  Logout,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { NAV_ITEMS, isPathActive } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import ThemeToggle from '../Theme/ThemeToggle';

/**
 * 모바일 드로어 내비게이션.
 * open / onOpenChange 를 넘기면 제어 모드(하단탭 "더보기" 와 연동),
 * 넘기지 않으면 내부 상태로 동작한다.
 */
const MobileNavigation = ({ open, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isDrawerOpen = isControlled ? open : internalOpen;
  const setIsDrawerOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    if (onOpenChange) onOpenChange(next);
  };
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDarkMode } = useThemeMode();
  const theme = useTheme();

  // 메인 네비게이션 메뉴 (단일 소스: constants/navigation.js)
  const mainMenuItems = NAV_ITEMS.map((item) => ({
    path: item.path,
    label: item.label,
    icon: <item.Icon />,
  }));

  // 관리자 메뉴 (admin 권한이 있을 때만)
  const adminMenuItems = [
    { path: '/admin', label: '시스템 관리', icon: <AdminPanelSettings /> },
  ];

  // 계정 메뉴
  const accountMenuItems = [
    { action: 'logout', label: '로그아웃', icon: <Logout /> },
  ];

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // 이동 항목은 <a href> 링크가 처리한다. 클릭 시 드로어만 닫는다.
  const handleMenuClick = (item) => {
    if (item.action === 'logout') {
      logout();
    }
    setIsDrawerOpen(false);
  };

  const isCurrentPath = (path) => isPathActive(path, location.pathname);

  const renderMenuItem = (item, isActive = false) => (
    <ListItem key={item.path || item.action} disablePadding>
      <ListItemButton
        {...(item.path ? { component: RouterLink, to: item.path } : {})}
        aria-current={isActive ? 'page' : undefined}
        onClick={() => handleMenuClick(item)}
        sx={{
          minHeight: 48,
          px: 2,
          py: 1.5,
          backgroundColor: isActive 
            ? alpha(theme.palette.primary.main, 0.1)
            : 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          },
          borderRadius: 1,
          mx: 1,
          my: 0.5,
        }}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: 40,
            color: isActive ? theme.palette.primary.main : 'inherit',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText 
          primary={item.label}
          sx={{
            '& .MuiListItemText-primary': {
              fontSize: '0.95rem',
              fontWeight: isActive ? 600 : 400,
              color: isActive ? theme.palette.primary.main : 'inherit',
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  const drawerContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 헤더 영역 */}
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            StackStock
          </Typography>
          <IconButton 
            onClick={handleDrawerToggle}
            aria-label="메뉴 닫기"
            size="small"
            sx={{ 
              color: theme.palette.text.secondary, 
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* 사용자 정보 */}
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            {(user?.nickname || user?.username)?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.nickname || user?.username || '사용자'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 메인 메뉴 */}
      <Box sx={{ flex: 1, py: 1 }}>
        <Typography 
          variant="overline" 
          sx={{ 
            px: 2, 
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 600, 
          }}
        >
          메인 메뉴
        </Typography>
        <List component="nav" aria-label="모바일 주 메뉴" sx={{ px: 0 }}>
          {mainMenuItems.map(item => 
            renderMenuItem(item, isCurrentPath(item.path)),
          )}
        </List>

        {/* 관리자 메뉴 (admin인 경우만) */}
        {user?.is_admin && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography 
              variant="overline" 
              sx={{ 
                px: 2, 
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
                fontWeight: 600, 
              }}
            >
              관리자
            </Typography>
            <List sx={{ px: 0 }}>
              {adminMenuItems.map(item => 
                renderMenuItem(item, isCurrentPath(item.path)),
              )}
            </List>
          </>
        )}
      </Box>

      {/* 하단 영역 - 테마 토글 및 계정 */}
      <Box
        sx={{
          mt: 'auto',
          borderTop: `1px solid ${theme.palette.divider}`,
          p: 2,
        }}
      >
        {/* 테마 토글 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            다크모드
          </Typography>
          <ThemeToggle />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* 계정 메뉴 */}
        <List sx={{ px: 0, py: 0 }}>
          {accountMenuItems.map(item => 
            renderMenuItem(item, item.path ? isCurrentPath(item.path) : false),
          )}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* 햄버거 메뉴 버튼 */}
      <IconButton
        color="inherit"
        aria-label="메뉴 열기"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ mr: 1 }}
      >
        <MenuIcon />
      </IconButton>

      {/* 모바일 네비게이션 드로어 */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        // keepMounted 를 쓰지 않는다: 항상 마운트된 Modal 은 열릴 때 FocusTrap 이 포커스를 서랍 안으로
        // 옮기지 못해(모달 매니저 등록 전에 effect 가 실행됨) 포커스가 햄버거에 남고 Escape 가 동작하지 않는다.
        // 마운트 방식이면 열릴 때 서랍 안으로 포커스, 닫힐 때 열기 버튼으로 복귀가 MUI 기본 동작으로 처리된다.
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: isDarkMode
              ? theme.palette.background.paper
              : theme.palette.background.default,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default MobileNavigation;
